import { HomeRecoveryGuide } from "@/components/home/HomeRecoveryGuide";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { ArrowRight, ChevronDown, ChevronUp, Eye, HelpCircle, Heart, LogIn, Leaf, ShieldCheck, Sparkles, Users } from "lucide-react";
import logoAsset from "@/assets/logo.png.asset.json";
import recoveryLineAsset from "@/assets/recovery-isnt-straight-line.png.asset.json";

import { addRipple } from "@/lib/ripple";
import { track } from "@/lib/track";
import { trackFunnel, startMembershipJourney, MEMBERSHIP_JOURNEY_PATH } from "@/lib/funnel";

import { LandingFooter } from "@/components/LandingFooter";
import { NewsletterSignup } from "@/components/NewsletterSignup";
import { SocialFollowSection } from "@/components/SocialFollowSection";
import { TestimonialsSection } from "@/components/TestimonialsSection";
import { TrustpilotStrip } from "@/components/TrustpilotStrip";
import { TrustpilotMiniCarousel } from "@/components/TrustpilotMiniCarousel";

import { WhoIsItForCarousel } from "@/components/WhoIsItForCarousel";

import { NeedsCheck } from "@/components/NeedsCheck";
import { FeelingsPathway } from "@/components/feelings/FeelingsPathway";
import { TraumaResponseWheel } from "@/components/feelings/TraumaResponseWheel";
import { useMembership } from "@/hooks/useMembership";




import { OnePlaceSection } from "@/components/home/OnePlaceSection";
import { HindiReadingSection } from "@/components/home/HindiReadingSection";
import { DutchReadingSection } from "@/components/home/DutchReadingSection";

import { HomeFaq } from "@/components/home/HomeFaq";
import { accentVars, type AccentName } from "@/lib/home-accents";

import { MembershipFeaturesSection } from "@/components/MembershipFeaturesSection";
import { TherapistOfferLine } from "@/components/home/TherapistOfferLine";

import { InspirationSection } from "@/components/InspirationSection";
import { FloatingCalm } from "@/components/FloatingCalm";
import { QuestionsCarousel } from "@/components/home/QuestionsCarousel";
import { PlatformMapSection } from "@/components/home/PlatformMapSection";
import { HomeRecoveryBoard } from "@/components/home/HomeRecoveryBoard";
import { HomeSanctuary } from "@/components/home/HomeSanctuary";

import { useLanguage } from "@/lib/language";





export const logo = logoAsset.url;

const HOME_OG =
  "https://storage.googleapis.com/gpt-engineer-file-uploads/attachments/og-images/bda540b3-55c7-4373-af6d-b5588ecb7c64";

export const HOME_TITLE = "Trauma Recovery, PTSD & Emotional Healing | Recovery Trauma™";
const HOME_DESC =
  "Survivor-led trauma recovery: plain-English guides to PTSD, C-PTSD and trauma responses, plus grounding, breathing and journaling tools for emotional healing.";

export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      { title: HOME_TITLE },
      { name: "description", content: HOME_DESC },
      { property: "og:title", content: HOME_TITLE },
      { property: "og:description", content: HOME_DESC },
      { property: "og:url", content: "https://recoverytrauma.com/" },
      { property: "og:image", content: HOME_OG },
      { name: "twitter:image", content: HOME_OG },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [
      { rel: "canonical", href: "https://recoverytrauma.com/" },
      { rel: "preload", as: "image", href: logo, fetchPriority: "high" },
    ],
  }),
  component: HomePage,
});

function Chapter({
  label,
  title,
  children,
}: {
  label: string;
  title?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="relative">
      <div className="mb-1.5 text-center sm:mb-2">
        <span className="text-[10px] font-semibold uppercase tracking-[0.28em] text-[var(--gold-deep)]">
          {label}
        </span>
        {title && (
          <h2 className="mt-1 font-display text-base leading-tight text-foreground sm:text-lg lg:font-['Outfit'] lg:text-3xl lg:font-semibold">
            {title}
          </h2>
        )}
      </div>
      {children}
    </section>
  );
}

/**
 * Soft, pastel section band — adds gentle colour and separation between
 * homepage chapters without shouting. Purely presentational.
 */
function TintedBand({
  accent,
  strength = "soft",
  children,
  className = "",
}: {
  accent: AccentName;
  /** Visual rhythm: alternate `soft` (cream) and `rich` (champagne) bands. */
  strength?: "soft" | "rich";
  children: React.ReactNode;
  className?: string;
}) {
  const top = strength === "rich" ? 100 : 62;
  const mid = strength === "rich" ? 45 : 22;
  return (
    <div
      style={{
        background: `linear-gradient(170deg, color-mix(in oklab, var(--acc-tint) ${top}%, transparent) 0%, color-mix(in oklab, var(--acc-tint) ${mid}%, transparent) 58%, transparent 100%)`,
        borderColor: `color-mix(in oklab, var(--acc-ink) ${strength === "rich" ? 26 : 18}%, transparent)`,
        boxShadow:
          "0 18px 40px -32px color-mix(in oklab, var(--acc-ink) 60%, transparent)",
        ...accentVars(accent),
      }}
      className={`rounded-[24px] border px-4 py-5 sm:px-6 sm:py-7 lg:rounded-[32px] lg:px-10 lg:py-10 ${className}`}
    >
      {children}
    </div>
  );
}




/**
 * Homepage emotion pathway. Members and admins get the full reflection
 * content; everyone else sees the preview with the membership invitation.
 */
function HomeFeelingsPathway() {
  const membership = useMembership();
  const hasAccess = membership.isMember || membership.isAdmin;
  return (
    <FeelingsPathway
      variant="full"
      showIntro
      compact
      desktopIntroBesideWheel
      hasMemberAccess={hasAccess}
    />
  );
}

function OpenFullPage({ to, label }: { to: "/trauma-response-wheel" | "/how-are-you-feeling"; label: string }) {
  return (
    <div className="mx-auto mt-3 flex max-w-xl justify-center">
      <Link
        to={to}
        className="inline-flex min-h-10 items-center gap-1.5 rounded-full border border-gold/50 px-4 py-2 text-sm font-medium text-[var(--gold-deep)] transition hover:bg-gold-light/40"
      >
        {label} →
      </Link>
    </div>
  );
}

function HomePage() {
  return (
    <div className="relative overflow-hidden">
      {/* Ambient background wash */}
      <div
        aria-hidden
        className="bg-blob-warm pointer-events-none absolute inset-x-0 top-0 -z-10 h-[980px] opacity-70"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute -start-32 top-[420px] -z-10 h-[520px] w-[520px] rounded-full bg-gold/10 blur-3xl"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute -end-40 top-[880px] -z-10 h-[560px] w-[560px] rounded-full bg-[var(--gold-deep)]/10 blur-3xl"
      />
      <FloatingCalm />



      <div className="relative z-10">
        <Hero />
      </div>

      <div className="relative z-10 mx-auto max-w-[1320px] space-y-4 px-4 pb-6 pt-1 sm:space-y-5 sm:px-6 sm:pt-2 lg:space-y-12 lg:px-8 lg:pb-12">


{/* Hindi-written articles — only shown on the Hindi pages */}
        <HindiReadingSection />

{/* Dutch-written articles — only shown on the Dutch pages */}
        <DutchReadingSection />


{/* Social follow — before Trustpilot */}
        <SocialFollowSection />

        {/* Trust signals — Trustpilot first, with the latest reviews */}
        <div className="lg:rounded-[32px] lg:border lg:border-gold/20 lg:bg-card/35 lg:px-10 lg:py-10 lg:shadow-sm">
          <TrustpilotStrip />
          <TrustpilotMiniCarousel />
        </div>

        {/* Questions you might be asking yourself — sits under Trustpilot */}
        <QuestionsCarousel />

        {/* Plain-English trauma recovery / PTSD / emotional healing guide */}
        <HomeRecoveryGuide />

        {/* Recovery Board — the gentle members' game */}
        <HomeRecoveryBoard />

        {/* The Healing Sanctuary — sits where the Halloween special used to be */}
        <HomeSanctuary />




        {/* Trauma Response Wheel — higher up, right after the live events */}
        <div className="mx-auto w-full max-w-5xl scroll-mt-16">
          <TraumaResponseWheel slim />
          <OpenFullPage to="/trauma-response-wheel" label="Open the Trauma Response Wheel page" />
        </div>

        {/* How are you feeling? — emotion wheel sits right after the community voices */}
        <div className="mx-auto w-full max-w-5xl lg:-mt-8">
          <HomeFeelingsPathway />
          <OpenFullPage to="/how-are-you-feeling" label="Open the Emotion Wheel page" />
        </div>


        {/* What you need right now — needs carousel, directly under the wheel */}
        <div className="lg:rounded-[32px] lg:border lg:border-gold/20 lg:bg-card/45 lg:px-10 lg:py-12">
          <NeedsCheck />
        </div>

        {/* Platform map — a friendly overview of everything, before the community voices */}
        <PlatformMapSection />

        {/* Community voices — sits directly before "Who is Recovery Trauma™ for?" */}
        <TestimonialsSection compact />

        {/* Is it relevant to me? */}
        <div className="lg:py-4">
          <WhoIsItForCarousel />
        </div>

        {/* Why Recovery Trauma™ */}
        <div className="lg:rounded-[32px] lg:bg-gold-light/15 lg:px-10 lg:py-12">
          <OnePlaceSection />
        </div>




        <MembershipFeaturesSection />

        <TherapistOfferLine />

        {/* Questions before you join */}
        <TintedBand accent="gold">
          <HomeFaq />
        </TintedBand>

        {/* Newsletter */}
        <div className="py-2">
          <NewsletterSignup source="homepage" />
        </div>

        {/* Final join CTA */}
        <InspirationSection />
      </div>


      <div className="relative z-10">
        <LandingFooter />
      </div>
      
    </div>
  );
}

/* ============================================================
   HERO — floating brand logo that gently shrinks toward the nav
   ============================================================ */
function useHeroLogoScroll() {
  const [p, setP] = useState(0);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return;
    let frame = 0;
    const onScroll = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(() => {
        frame = 0;
        setP(Math.min(1, Math.max(0, window.scrollY / 260)));
      });
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, []);

  return p;
}

function Hero() {
  const p = useHeroLogoScroll();
  const [moreOpen, setMoreOpen] = useState(false);
  const { lang } = useLanguage();
  const membership = useMembership();
  const hasMemberAccess = membership.isMember || membership.isAdmin;
  // While the auth check is still resolving we keep the guest view — the
  // header swaps Log in → My Account once the session is known, so there is
  // no wrong-button flash for signed-in members.
  const authed = membership.authResolved && membership.isAuthed;
  const joinLabel = {
    en: "Join",
    de: "Mitglied werden",
    fr: "Rejoindre",
    es: "Unirse",
    nl: "Deelnemen",
    "zh-cn": "加入",
    ar: "انضم",
    hi: "जुड़ें",
  }[lang];
  const dashLabel = {
    en: "My Dashboard",
    de: "Mein Dashboard",
    fr: "Mon tableau de bord",
    es: "Mi panel",
    nl: "Mijn dashboard",
    "zh-cn": "我的面板",
    ar: "لوحتي",
    hi: "मेरा डैशबोर्ड",
  }[lang];

  return (
    <section className="relative">
      <div className="mx-auto max-w-6xl px-3 pb-2 pt-2 sm:px-6 sm:pt-3 lg:max-w-[1320px] lg:px-8 lg:pb-4 lg:pt-6">
        <div className="relative grid grid-cols-1 gap-x-8 overflow-hidden rounded-[24px] border border-gold/30 bg-card/70 px-4 py-5 shadow-sm backdrop-blur-sm md:grid-cols-[minmax(0,0.92fr)_minmax(20rem,1.08fr)] md:grid-rows-[auto_1fr] md:px-7 md:py-7 lg:min-h-[calc(100vh-16rem)] lg:content-center lg:grid-cols-[minmax(0,1.08fr)_minmax(0,0.92fr)] lg:grid-rows-[auto_auto] lg:items-center lg:gap-x-16 lg:rounded-[36px] lg:px-16 lg:py-8">
          <div className="order-1 flex min-w-0 flex-col items-center text-center md:items-start md:text-start">
            {/* Floating brand mark — shrinks and fades toward the sticky nav logo */}
            <div className="relative mb-2 flex flex-col items-center gap-2 md:items-start">
            <span
              aria-hidden
              className="absolute start-1/2 top-1/2 h-24 w-24 -translate-x-1/2 -translate-y-1/2 rounded-full bg-gold/15 blur-2xl md:start-8"
              style={{ opacity: 0.55 * (1 - p) }}
            />
            <img
              src={logo}
              alt="Recovery Trauma™"
              width={200}
              height={200}
              fetchPriority="high"
              decoding="async"
              className="relative h-14 w-14 animate-gold-float rounded-full object-cover ring-2 ring-gold/50 shadow-md will-change-transform sm:h-16 sm:w-16"
              style={{
                transform: `translateY(${-36 * p}px) scale(${1 - 0.55 * p})`,
                opacity: 1 - 0.85 * p,
              }}
            />
            <span className="flex flex-wrap items-center justify-center gap-1.5 md:justify-start">
              <span className="inline-flex items-center gap-1 rounded-full border border-gold/30 bg-gold-light/30 px-2.5 py-1 text-[10px] font-medium uppercase tracking-[0.14em] text-[var(--gold-deep)]">
                <Sparkles className="h-3 w-3" /> Survivor-led · Trauma-informed
              </span>
            </span>
          </div>

          <h1 className="font-display text-[1.875rem] leading-[1.05] tracking-tight text-foreground sm:text-4xl lg:font-['Outfit'] lg:text-[4.35rem] lg:font-semibold lg:leading-[0.98]">
            Recovery Trauma<span className="text-[var(--gold-deep)]">™</span>
            <span className="mt-1.5 block font-display text-lg italic leading-snug tracking-normal text-[var(--gold-deep)] sm:text-xl lg:mt-4 lg:font-['Figtree'] lg:text-[1.85rem] lg:font-medium lg:not-italic">
              A Safe Place to Learn, Recover and Grow
            </span>
          </h1>

          <span
            aria-hidden
            className="mt-3 block h-[3px] w-16 rounded-full bg-[var(--gradient-gold)] opacity-70"
          />

          <p className="mt-3 max-w-lg text-sm leading-relaxed text-foreground/85 sm:text-base lg:mt-6 lg:max-w-xl lg:font-['Figtree'] lg:text-xl lg:leading-relaxed">
            Practical trauma education, recovery tools and support — together in one safe, survivor-led space.
          </p>

          <button
            type="button"
            aria-expanded={moreOpen}
            aria-controls="home-hero-more"
            onClick={() => setMoreOpen((open) => !open)}
            className="mt-2 inline-flex min-h-11 items-center gap-1.5 self-center text-sm font-semibold text-[var(--gold-deep)] underline decoration-gold/60 underline-offset-4 transition hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring md:self-start lg:hidden"
          >
            {moreOpen ? "Show less" : "Read more"}
            {moreOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>

          <div
            id="home-hero-more"
            className={`mt-2 max-w-lg space-y-2 border-s-2 border-gold/30 ps-3 text-sm leading-relaxed text-foreground/75 lg:mt-6 lg:ps-5 lg:text-base ${moreOpen ? "block" : "hidden lg:block"}`}
          >
            <p>Created from lived experience. Built to help you understand, explore and find what supports you.</p>
            <p>Healing from trauma can feel overwhelming. Finding the right information, tools and support shouldn't be.</p>
            <p>Recovery Trauma™ brings trauma education, practical recovery tools, guided self-reflection, community, workshops and therapist discovery together in one calm, survivor-led space.</p>
            <p>Learn at your own pace. Use what you need. Come back whenever you're ready.</p>
          </div>
          </div>

          <figure className="order-3 mx-auto mt-6 w-full max-w-[18rem] self-center sm:max-w-[21rem] md:order-2 md:col-start-2 md:row-span-2 md:row-start-1 md:mt-0 md:max-w-[31rem] lg:max-w-[34rem]">
            <img
              src={recoveryLineAsset.url}
              alt="Hand-drawn illustration: 'Recovery isn't a straight line. It's okay to take your time.' — friendly characters walking a winding path, some days lighter, some heavier, every step still counts."
              width={1024}
              height={1024}
              decoding="async"
              fetchPriority="high"
              sizes="(min-width: 768px) 48vw, (min-width: 640px) 21rem, 18rem"
               className="block h-auto w-full rounded-[20px] border border-gold/25 object-cover align-bottom shadow-sm lg:rounded-[28px] lg:shadow-[0_28px_70px_-38px_var(--gold-deep)]"
            />
            <figcaption className="mx-auto mt-2 max-w-md text-center text-[11px] italic leading-relaxed text-muted-foreground sm:text-xs">
              Recovery isn't a straight line — every small step forward, sideways, or back still counts.
            </figcaption>
          </figure>

          {/* Join (primary) + Log In (secondary). The iPhone shell keeps its own
              "Create Account" button, which CSS hides on the website. */}
          <div data-no-overlap="" className="order-2 mx-auto mt-5 flex w-full max-w-md flex-col items-stretch gap-3 md:order-3 md:max-w-none md:gap-3 lg:mt-8 lg:flex-row lg:flex-wrap lg:items-center lg:justify-start lg:gap-3">
            {/* The iPhone app needs an immediate, unmistakable registration
                path. CSS reveals this only when the native shell is present. */}
            {!authed && (
              <Link
                to="/signup"
                onPointerDown={addRipple}
                onClick={() => {
                  track("cta_click", { cta: "create_account", platform: "ios" }, { source: "home_hero" });
                  startMembershipJourney("create_account", "home_hero");
                }}
                className="native-account-cta btn-gold group inline-flex min-h-[52px] w-full shrink-0 justify-center px-6! text-center uppercase lg:w-auto lg:flex-none lg:min-h-14 lg:px-8!"
              >
                <span data-no-translate className="relative z-10 whitespace-nowrap text-sm tracking-[0.04em]">
                  Create Account
                </span>
                <ArrowRight className="relative z-10 h-4 w-4 shrink-0 transition-transform group-hover:translate-x-1" strokeWidth={2.4} />
              </Link>
            )}
            {/* The dominant path. Signed-in members go straight to their
                dashboard instead of the payment flow. */}
            <Link
              to={hasMemberAccess ? "/dashboard" : MEMBERSHIP_JOURNEY_PATH}
              onPointerDown={addRipple}
              onClick={() => {
                if (hasMemberAccess) {
                  track("cta_click", { cta: "my_dashboard" }, { source: "home_hero" });
                } else {
                  track("cta_click", { cta: "join", flow: "membership_journey" }, { source: "home_hero" });
                  startMembershipJourney("join", "home_hero");
                }
              }}
              className="web-membership-cta btn-gold group inline-flex min-h-[52px] w-full justify-center px-6! text-center uppercase lg:min-h-14 lg:w-auto lg:flex-none lg:px-8!"
            >
              <span data-no-translate className="relative z-10 whitespace-nowrap text-sm tracking-[0.04em]">{hasMemberAccess ? dashLabel : joinLabel}</span>
              <ArrowRight className="relative z-10 h-4 w-4 flex-none transition-transform group-hover:translate-x-1" strokeWidth={2.4} />
            </Link>

            {/* Log In — for people who already have an account. */}
            {!authed && (
              <Link
                to="/login"
                onPointerDown={addRipple}
                onClick={() => track("cta_click", { cta: "login" }, { source: "home_hero" })}
                className="inline-flex min-h-[52px] w-full items-center justify-center gap-2 whitespace-nowrap rounded-full border border-[var(--gold-deep)]/45 bg-card/70 px-6 text-[0.82rem] font-semibold uppercase tracking-[0.08em] text-[var(--gold-deep)] transition-all duration-200 hover:border-[var(--gold-deep)]/80 hover:bg-gold-light/30 active:scale-[0.98] lg:min-h-14 lg:w-auto lg:flex-none lg:px-7"
              >
                <LogIn className="h-4 w-4" strokeWidth={2.1} aria-hidden />
                Log In
              </Link>
            )}

            {/* Quiet secondary row: explore what membership includes, or look
                around as a guest. Neither competes with the gold Join button. */}
            <div className="mt-1 flex w-full flex-wrap items-center justify-center gap-x-6 gap-y-0.5 lg:mt-0 lg:w-auto lg:justify-start">
              {!hasMemberAccess && (
                <Link
                  to="/membership-benefits"
                  onPointerDown={addRipple}
                  onClick={() => track("cta_click", { cta: "explore_membership" }, { source: "home_hero" })}
                  className="inline-flex min-h-11 items-center gap-1.5 text-sm font-medium text-[var(--gold-deep)] underline decoration-gold/50 underline-offset-4 transition hover:text-foreground hover:decoration-gold"
                >
                  <Sparkles className="h-4 w-4" strokeWidth={2} aria-hidden />
                  Explore Membership
                </Link>
              )}
              <Link
                to="/explore"
                onPointerDown={addRipple}
                onClick={() => track("cta_click", { cta: "guest_access" }, { source: "home_hero" })}
                className="inline-flex min-h-11 items-center gap-1.5 text-sm font-medium text-[var(--gold-deep)] underline decoration-gold/50 underline-offset-4 transition hover:text-foreground hover:decoration-gold"
              >
                <Eye className="h-4 w-4" strokeWidth={2} />
                {authed ? "Explore" : "Explore as Guest"}
              </Link>
            </div>
          </div>
        </div>

        <div className="mx-auto mt-2 flex max-w-4xl flex-col items-center lg:mt-5 lg:max-w-[1180px] lg:flex-row lg:items-center lg:justify-start lg:gap-5 lg:rounded-full lg:border lg:border-gold/25 lg:bg-card/65 lg:px-7 lg:py-2 lg:shadow-sm">
          <div className="flex w-full flex-col items-center lg:w-auto lg:flex-none lg:items-start">
            <Link
              to="/about"
              hash="meet-the-founder"
              onPointerDown={addRipple}
              onClick={() => track("cta_click", { cta: "meet_the_founder" }, { source: "home_hero" })}
              className="group inline-flex min-h-10 items-center justify-center gap-2 rounded-full border border-gold/35 bg-cream/70 px-4 py-2 text-sm font-semibold text-[var(--gold-deep)] shadow-[0_6px_18px_-14px_var(--gold-deep)] transition-all duration-200 hover:-translate-y-0.5 hover:border-gold/60 hover:bg-gold-light/35 hover:shadow-[0_10px_22px_-14px_var(--gold-deep)] active:translate-y-0"
            >
              <Heart className="h-4 w-4 fill-[oklch(0.62_0.2_15)] text-[oklch(0.62_0.2_15)]" strokeWidth={1.9} aria-hidden />
              Meet the Founder
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" strokeWidth={1.9} />
            </Link>
            <p className="mt-1.5 max-w-xs text-center font-display text-[13px] italic leading-snug text-muted-foreground lg:hidden">
              “I built the platform I once searched for myself.”
              <span className="mt-0.5 block text-xs font-medium not-italic text-[var(--gold-deep)]">— Jana Burkhardt, Founder</span>
            </p>
          </div>

          <div className="mt-2 flex flex-nowrap items-center justify-center gap-2 sm:gap-3 lg:mt-0">
            <QuietLink to="/faq" cta="faq" icon={<HelpCircle className="h-3.5 w-3.5" />} label="FAQ" />
            <QuietLink
              to="/crisis"
              cta="crisis_support"
              icon={
                <span
                  aria-hidden
                  className="h-1.5 w-1.5 animate-pulse rounded-full bg-[oklch(0.62_0.16_25)]"
                />
              }
              label="Crisis Support"
              tone="rose"
            />
          </div>

          <p className="mt-2 flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-[10px] text-foreground/60 sm:text-[11px] lg:mt-0 lg:ml-auto lg:text-xs">
            <span className="inline-flex items-center gap-1.5">
              <Users className="h-3 w-3 text-[var(--gold-deep)]" /> 380,000+ following
            </span>
            <span className="inline-flex items-center gap-1.5">
              <ShieldCheck className="h-3 w-3 text-[var(--gold-deep)]" /> Survivor-led
            </span>
            <span className="inline-flex items-center gap-1.5">
              <Leaf className="h-3 w-3 text-[var(--gold-deep)]" /> Trauma-informed
            </span>
          </p>
        </div>
        <p className="mx-auto mt-2 hidden max-w-[1180px] px-7 text-start font-['Outfit'] text-lg italic text-muted-foreground lg:block">
          “I built the platform I once searched for myself.”
          <span className="ml-2 text-base font-medium not-italic text-[var(--gold-deep)]">— Jana Burkhardt, Founder</span>
        </p>
      </div>
    </section>
  );
}

function QuietLink({
  to,
  cta,
  icon,
  label,
  tone = "gold",
}: {
  to: string;
  cta: string;
  icon: React.ReactNode;
  label: string;
  tone?: "gold" | "rose";
}) {
  const toneClass =
    tone === "rose"
      ? "border-[oklch(0.64_0.13_25)]/30 bg-[oklch(0.96_0.02_25)]/80 font-semibold text-[oklch(0.48_0.15_25)] hover:bg-[oklch(0.93_0.04_25)]/90 dark:bg-[oklch(0.28_0.06_25)]/40 dark:text-[oklch(0.85_0.07_25)]"
      : "border-border/60 bg-muted/60 text-muted-foreground hover:bg-muted";
  return (
    <Link
      to={to}
      onPointerDown={addRipple}
      onClick={() => track("cta_click", { cta }, { source: "home_hero" })}
      className={`inline-flex min-h-[44px] shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border px-3 text-xs font-medium backdrop-blur-sm transition-all duration-200 hover:-translate-y-0.5 active:scale-[0.98] sm:gap-2 sm:px-6 sm:text-sm ${toneClass}`}
    >

      {icon}
      {label}
    </Link>
  );
}

